import React from "react";
import {
  WSLoader,
  WSPanel,
  WSSectionToolbar
} from "@wingspanhq/fe-component-library";
import { ListLedgerAccountsFilterParameterClassificationEq } from "@wingspanhq/wingspan-api-sdk";
import {
  queriesAccountingConnections,
  queriesAccountingReferenceData
} from "@query";
import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import { selectorIsQuickbooksConnectionSynced } from "../../../Integrations/selectors/selectorIsQuickbooksConnectionSynced";
import { selectorQuickbooksConnection } from "../../../Integrations/selectors/selectorQuickbooksConnection";
import { IntegrationsSection } from "./index";
import { IntegrationResourceType } from "./types";

type IntegrationWidgetProps = {
  isInactive: boolean;
  payeeId: string;
  vendorId?: string;
  expenseAccountId?: string;
  engagementId?: string;
};

/**
 * The contractor's QuickBooks vendor / expense-account mapping — the payee twin
 * of [[PayerIntegrationWidget]], and structured identically.
 *
 * v3 has no dedicated QuickBooks service endpoint: the connection is derived from
 * `listAccountingConnections` (see [[selectorQuickbooksConnection]]) and the
 * selectable vendors / expense accounts come from that connection's reference
 * data — expense accounts being the ledger accounts classified `Expense`, since
 * v3 replaced v1's per-class endpoints with one filtered collection. The mapped
 * ids themselves are NOT read here; they belong to the payee or the assignment
 * and are passed in by the screen.
 */
export const PayeeIntegrationWidget: React.FC<
  IntegrationWidgetProps
> = props => {
  const queryFeatureFlags = useQueryFeatureFlags();
  // The list filter accepts `status` only, so the partner is narrowed client-side.
  const queryConnections =
    queriesAccountingConnections.listAccountingConnections.useQuery({});
  const connection = selectorQuickbooksConnection(queryConnections.data);
  const connectionId = connection?.id;

  const isQuickbooksActive =
    !!connection &&
    (queryFeatureFlags.data?.forceShowQBO ||
      selectorIsQuickbooksConnectionSynced(connection));

  const isReferenceDataEnabled = isQuickbooksActive && !!connectionId;

  const queryVendors =
    queriesAccountingReferenceData.listAccountingVendors.useQueryAll(
      { connectionId: connectionId as string },
      { enabled: isReferenceDataEnabled }
    );
  const queryExpenseAccounts =
    queriesAccountingReferenceData.listLedgerAccounts.useQueryAll(
      {
        connectionId: connectionId as string,
        filter: {
          classification: {
            eq: ListLedgerAccountsFilterParameterClassificationEq.EXPENSE
          }
        }
      },
      { enabled: isReferenceDataEnabled }
    );

  if (!isQuickbooksActive) {
    return null;
  }

  if (queryVendors.isLoading || queryExpenseAccounts.isLoading) {
    return (
      <>
        <WSSectionToolbar
          title="Integration mappings"
          button={{ label: "Edit", disabled: true }}
        />

        <WSPanel mb="XL">
          <WSLoader.Spinner size="M" />
        </WSPanel>
      </>
    );
  }

  // A reference read that errors leaves the panel out entirely rather than
  // rendering every mapping as "Not mapped", which would read as data loss.
  if (queryVendors.isError || queryExpenseAccounts.isError) {
    return null;
  }

  const vendors = queryVendors.flatData ?? [];
  const expenseAccounts = queryExpenseAccounts.flatData ?? [];

  return (
    <IntegrationsSection
      isInactive={props.isInactive}
      resourceType={IntegrationResourceType.Payee}
      payeeIntegration={{
        payeeId: props.payeeId,
        engagementId: props.engagementId,
        vendorId: props.vendorId,
        expenseAccountId: props.expenseAccountId,
        vendor: vendors.find(vendor => vendor.id === props.vendorId),
        expenseAccount: expenseAccounts.find(
          account => account.id === props.expenseAccountId
        ),
        defaultAccount: expenseAccounts.find(
          account =>
            account.id === connection?.mappingDefaults?.expenseLedgerAccountId
        )
      }}
    />
  );
};
